// In-flight request deduplication + automatic Idempotency-Key generation.
// See: docs/performance.md (Frente 1)

// Map<signature, Promise> — requests currently in flight.
const inFlight = new Map();

/**
 * Generate a stable signature for a request so two identical calls fired in
 * rapid succession can share the same in-flight promise.
 */
export function signatureFor(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  const params = config.params ? JSON.stringify(config.params) : "";
  // axios serialises config.data to a string before the request; here it may
  // still be an object if we're in the request interceptor.
  const body =
    typeof config.data === "string"
      ? config.data
      : config.data
      ? JSON.stringify(config.data)
      : "";
  return `${method}|${url}|${params}|${body}`;
}

/**
 * Register a request as in-flight. Returns a disposer to clear it.
 */
export function trackInFlight(sig, promise) {
  inFlight.set(sig, promise);
  const clear = () => {
    if (inFlight.get(sig) === promise) inFlight.delete(sig);
  };
  // We attach both handlers so the entry is cleared in success AND error.
  promise.then(clear, clear);
  return clear;
}

export function findInFlight(sig) {
  return inFlight.get(sig);
}

/**
 * Copia de la respuesta para los callers que se enganchan a una petición
 * ya en vuelo: cada caller recibe su propio `data` (varios slices mutan o
 * congelan `response.data` — compartir la referencia provocaba
 * "Cannot assign to read only property").
 */
export function cloneResponse(res) {
  if (!res || typeof res !== "object") return res;
  let data = res.data;
  if (data && typeof data === "object") {
    try {
      const sc = typeof window !== "undefined" ? window.structuredClone : undefined;
      data = typeof sc === "function" ? sc(data) : JSON.parse(JSON.stringify(data));
    } catch {
      /* data no clonable → se comparte */
    }
  }
  return { ...res, data };
}

/**
 * Dedupe de GETs idénticos en vuelo (mismo url + params). Nunca se aplica
 * a mutaciones: un POST repetido lo resuelve la idempotencia del backend,
 * no el cliente. `send(url, config)` es el `get` original de axios.
 * Escape: `{ skipDedupe: true }`.
 */
// Fase 5 · Observador opcional de GETs fusionados (el panel de depuración
// los muestra como duplicados evitados). Sin observador, coste cero.
let coalesceObserver = null;
export function setDedupeObserver(fn) {
  coalesceObserver = typeof fn === "function" ? fn : null;
}

export function createGetDeduper(send, clone = cloneResponse) {
  const pending = new Map();
  return function dedupedGet(url, config = {}) {
    const sig = signatureFor({ method: "get", url, params: config?.params });
    if (config && config.skipDedupe) {
      // Petición fresca (tras un evento o una escritura): no se une a la que
      // está en vuelo (pudo salir ANTES del cambio) y pasa a ser la que
      // comparten los GET idénticos que lleguen después.
      const fresh = Promise.resolve().then(() => send(url, config));
      pending.set(sig, fresh);
      const clearFresh = () => {
        if (pending.get(sig) === fresh) pending.delete(sig);
      };
      fresh.then(clearFresh, clearFresh);
      return fresh;
    }
    const existing = pending.get(sig);
    if (existing) {
      if (coalesceObserver) {
        try { coalesceObserver({ url, params: config?.params }); } catch { /* noop */ }
      }
      return existing.then(clone);
    }
    const promise = Promise.resolve().then(() => send(url, config));
    pending.set(sig, promise);
    const clear = () => {
      if (pending.get(sig) === promise) pending.delete(sig);
    };
    promise.then(clear, clear);
    return promise;
  };
}

/**
 * Generate a UUID v4 (RFC4122).
 * Uses crypto.randomUUID if available (all modern browsers); fallback otherwise.
 */
export function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Mutating HTTP methods that should carry an Idempotency-Key header.
 * DELETE is usually already idempotent by path; we still add the key so the
 * backend can short-circuit duplicates fired in the same second.
 */
const MUTATING = new Set(["post", "put", "patch", "delete"]);

export function isMutatingMethod(method) {
  return MUTATING.has((method || "").toLowerCase());
}
